/**
 * Once-per-problem reporting for an authored field a block refuses. Shared by
 * Content Ribbon and In-Page Legal, so the two cannot drift in how they key,
 * bound or forget what they have said. Each caller passes its own log, and
 * decides for itself which builds report.
 */

/** How many problems a reporter remembers at once; the oldest is forgotten first. */
export const MOST_PROBLEMS_REMEMBERED = 200;

/** What a problem is remembered by: the field, and what was wrong with it.
 *  ⚠ THE VALUE AND NOT ONLY THE FIELD: one server process draws every block on
 *  the site, so a second block wrong in a DIFFERENT way is a second authoring
 *  mistake, and keying on the field alone left it silent. Cut, which bounds
 *  what ONE key costs and not how many there can be. */
const LONGEST_KEY_VALUE = 500;

/** What one figure inside a key is written as. ⚠ THE ONES JSON CANNOT HOLD ARE
 *  SPELLED RATHER THAN DROPPED: NaN, Infinity, null and a hole in a list all
 *  serialise to `null`, so four lists wrong in four different ways would share
 *  one key and only the first of them would speak. And behind a NUL a feed
 *  cannot carry, so the word "NaN" and the figure do not key alike. */
const HELD_MARK = "\u0000";

function held(_at: string, figure: unknown): unknown {
  if (typeof figure === "number" && !Number.isFinite(figure)) {
    return `${HELD_MARK}${figure}`;
  }

  return figure === undefined ? `${HELD_MARK}undefined` : figure;
}

export function problemKey(field: string, value: unknown): string {
  /* ⚠ SERIALISED AND NOT STRINGIFIED: `String([])` and `String([""])` are
     both the empty string, so two lists empty in different ways would share a
     key and the second block would inherit the first's silence.
     ⚠ AND INSIDE A try, BECAUSE JSON.stringify THROWS RATHER THAN RETURNING
     NOTHING on a BigInt or a cycle: a key may be read by a diagnostic that is
     not gated on the build, so an exception raised here is a failed server
     render rather than a message nobody reads. */
  let written: string;

  try {
    written = JSON.stringify(value, held) ?? String(value);
  } catch {
    /* ⚠ A SENTINEL AND NOT A SECOND CONVERSION: `String(value)` and
       `Object.prototype.toString.call(value)` each run code the value owns —
       its own `toString`, a proxy trap on `Symbol.toStringTag` — and would
       throw again on the same path the `try` was written for. `typeof` reads
       none of it, and costs one shared key for every value that lands here. */
    written = typeof value;
  }

  return `${field}:${written?.slice?.(0, LONGEST_KEY_VALUE) ?? ""}`;
}

export interface OnceReporter {
  /** Says `message` through the caller's log unless `key` was said already. */
  report: (key: string, message: string) => void;
  /** Forgets what has been said, so a suite reads its own message rather than
   *  an earlier test's silence. Does nothing on a production build. */
  forget: () => void;
}

/** A reporter with its own memory, speaking through `log`.
 *  ⚠ ONCE PER PROBLEM RATHER THAN PER RENDER: a block is drawn on the server,
 *  where every request is another render. Bounded by evicting the OLDEST key —
 *  dropping the newest instead would leave the block silent about every
 *  mistake made after the set filled up. */
export function onceReporter(log: (message: string) => void): OnceReporter {
  const reported = new Set<string>();

  return {
    report(key, message) {
      if (reported.has(key)) {
        return;
      }

      if (reported.size >= MOST_PROBLEMS_REMEMBERED) {
        reported.delete(reported.values().next().value);
      }

      reported.add(key);
      log?.(message);
    },
    forget() {
      if (process.env.NODE_ENV === "production") {
        return;
      }

      reported.clear();
    }
  };
}
